import { DashboardLayout, BaseLayout } from "@layouts/index";
import {
  getProfile,
  getProperties,
  getAvailableProperties,
  searchPropertiesByKeyword,
} from "@services/krent.service";
import { useJsApiLoader } from "@react-google-maps/api";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { useSessionStorage } from "../../hooks";
import PopularLocation from "components/PopularLocation";
import { NewsListResponse } from "@interfaces/news.interface";
import { getPageFromSessionStorage, toExploreSearchState } from "@utils/helpers";
import GetMeta from "@pages/SeoPages/GetMeta";
import { ExploreSearchSection } from "components/explore/ExploreSearchSection";
import { ExplorePropertyList } from "components/explore/ExplorePropertyList";
import { TransactionModal } from "components/explore/TransactionModal";
import { ExploreFiltersOffcanvas } from "components/explore/ExploreFiltersOffcanvas";
import { ExploreBlogSection } from "components/explore/ExploreBlogSection";
import KycBanner from "components/KycBanner";
import customFetch from "@services/axio";
import {
  getExploreArticleKey,
  getExploreBlogHeading,
  getExploreListingTitle,
  isExplorePriceKey,
  normalizeExploreQueryParams,
} from "@utils/explorePageContext";

export const Explore = () => {
  const [user] = useSessionStorage("user", {});
  const [userPendingTransactions, setUserPendingTransactions] = useState<any>();

  const [queryParams] = useSearchParams();
  const location: any = useLocation();
  const params = new URLSearchParams(location.search);
  const [intent, setIntent] = useState(params.get("intent") || "shortlet");
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env["REACT_APP_GOOGLE_API_KEY"] || "",
    libraries: ["places"],
  });
  const isTourMode = queryParams.get("tour") === "true";
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<any>(parseInt(getPageFromSessionStorage()));
  const [news, setNews] = useState<NewsListResponse[]>([]);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [inprogressTransactions, setInprogressTransactions] = useState(0);
  const [pendingInspectionTransactions, setPendingInspectionTransactions] =
    useState(0);
  const [pendingPaymentTransactions, setPendingPaymentTransactions] =
    useState(0);
  const [userKyc, setUserKyc] = useState();
  const [loadingkyc, setLoadingkyc] = useState(false);
  const limit: any = 12;
  const [data, setData] = useState<any>({
    properties: [],
    suggestions: [],
    current_page: 1,
    page_count: 0,
    last_page: 1,
    total: 0,
    numOfPages: 1,
  });

  const [autoCompleteLocation, setAutoCompleteLocation] = useState<string>("");
  const [autoCompleteState, setAutoCompleteState] = useState<any>(null);
  const onLoadState = (autocomplete: any) => {
    setAutoCompleteState(autocomplete);
  };
  const getPersonalKycStatus = async () => {
    setLoadingkyc(true);
    try {
      const resp = await customFetch.get("/users/kyc?type=PERSONAL");
      setUserKyc(resp?.data?.data);
    } catch (error: any) {}
    setLoadingkyc(false);
  };
  useEffect(() => {
    getPersonalKycStatus();
  }, [user]);

  useEffect(() => {
    const hasClosed = localStorage.getItem("transactionModalClosed");
    if (hasClosed === "false") {
      setShowModal(true);
    }
  }, [showModal]);

  const handleCloseModal = () => {
    setShowModal(false);
    localStorage.setItem("transactionModalClosed", "true");
  };

  const onStateChanged = () => {
    if (autoCompleteState !== null) {
      const state =
        autoCompleteState
          .getPlace()
          .address_components?.find((p: any) =>
            p.types.includes("sublocality_level_1"),
          )?.long_name ||
        autoCompleteState
          .getPlace()
          .address_components?.find((p: any) =>
            p.types.includes("neighborhood"),
          )?.long_name ||
        autoCompleteState
          .getPlace()
          .address_components?.find((p: any) => p.types.includes("locality"))
          ?.long_name ||
        autoCompleteState
          .getPlace()
          .address_components?.find((p: any) =>
            p.types.includes("administrative_area_level_1"),
          )?.long_name;

      setAutoCompleteLocation(state);
    } else {
      console.log("Autocomplete is not loaded yet!");
    }
  };
  const fetchProperties = () => {
    setLoading(true);

    const key = queryParams.get("key");
    let abortController = new AbortController();

    const handleResponse = (res: any) => {
      setData(toExploreSearchState(res));
    };

    if (key && isExplorePriceKey(key)) {
      const params = normalizeExploreQueryParams(queryParams);
      if (isTourMode) {
        params["tour"] = "true";
      }

      const requestParams: { [x: string]: string } = {
        ...params,
        limit: String(limit),
        page: String(page),
      };

      getProperties(requestParams, abortController.signal)
        .then(handleResponse)
        .finally(() => setLoading(false));

      return;
    }

    if (key) {
      const requestParams: { [x: string]: string } = {
        key,
        limit: String(limit),
        page: String(page),
      };

      if (isTourMode) {
        requestParams["tour"] = "true";
      }

      getAvailableProperties(requestParams, abortController.signal)
        .then(handleResponse)
        .finally(() => setLoading(false));

      return;
    }

    const params = normalizeExploreQueryParams(queryParams);
    if (isTourMode) {
      params["tour"] = "true";
    }

    const requestParams: { [x: string]: string } = {
      ...params,
      limit: String(limit),
      page: String(page),
      intent,
    };

    getProperties(requestParams, abortController.signal)
      .then(handleResponse)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user || !user._id) return;

    let abortController: AbortController = new AbortController();
    getProfile(abortController.signal)
      .then((re: any) => {
        setUserPendingTransactions(re?.data);
      })
      .catch((error: any) => {
        console.log("Error: ", error);
      });
  }, [user]);

  useEffect(() => {
    if (user.role !== "user") return;
    if (!userPendingTransactions) return;

    const { pendingTransactions } = userPendingTransactions;
    if (!pendingTransactions) return;

    const inprogress = pendingTransactions.filter(
      (item: any) => item.status === "progress",
    );
    const pendingInspection = pendingTransactions.filter(
      (item: any) => item.status === "awaiting-inspection",
    );
    const pendingPayment = pendingTransactions.filter(
      (item: any) => item.status === "customer-accepted",
    );

    const inprogressCount = inprogress.length > 0 ? inprogress[0].count : 0;
    const pendingInspectionCount =
      pendingInspection.length > 0 ? pendingInspection[0].count : 0;
    const pendingPaymentCount =
      pendingPayment.length > 0 ? pendingPayment[0].count : 0;

    setInprogressTransactions(inprogressCount);
    setPendingInspectionTransactions(pendingInspectionCount);
    setPendingPaymentTransactions(pendingPaymentCount);
  }, [userPendingTransactions]);

  useEffect(() => {
    if (location.state?.searchResult && location?.state !== null) {
      setData(toExploreSearchState(location?.state?.searchResult));
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.searchResult && location?.state !== null) {
      const fetchByPage = async () => {
        try {
          const checkInDate = location?.state?.checkInDate
            ? new Date(location.state.checkInDate).toISOString().split("T")[0]
            : "";
          const checkOutDate = location?.state?.checkOutDate
            ? new Date(location.state.checkOutDate).toISOString().split("T")[0]
            : "";

          const params = new URLSearchParams(location.search);
          const isTourMode =
            params.get("t") === "tour" || params.get("tour") === "true";

          const result = await searchPropertiesByKeyword(
            location?.state?.keyWord || "",
            checkInDate,
            checkOutDate,
            page,
            isTourMode ? { tour: "true" } : undefined,
            limit,
          );

          setData(toExploreSearchState(result));
        } catch (error) {
          console.log("Error during search:", error);
        }
      };
      fetchByPage();
    }
  }, [page, location.search, location.state]);

  useEffect(() => {
    if (!location.state?.searchResult || location.state === null) {
      fetchProperties();
    }
  }, [queryParams, location.pathname, page, intent]);
  useEffect(() => {
    document.body.style.overflow = "auto";

    document.body.style.pointerEvents = "auto";
  }, []);

  useEffect(() => {
    let abortController = new AbortController();

    const nNews = async () => {
      const url = process.env["REACT_APP_BLOG_BASE_URL"];

      try {
        const res = await fetch(`${url}`);
        let result = await res.json();
        setNews(result);
        return result;
      } catch (error) {
        console.log(error);
      }
    };
    nNews();
    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (location.state?.filter) {
      const offcanvasButton: any = document.querySelector(
        '[data-bs-toggle="offcanvas"]',
      );
      if (offcanvasButton) {
        offcanvasButton.click();
      }
    }
  }, []);

  const handleTypeChange = (newType: string) => {
    // Convert to correct intent values
    let intent = newType;
    if (newType === "rent") {
      intent = "long lease";
    }
    setIntent(intent);
    setPage(1);
  };

  const listingTitle = getExploreListingTitle(
    intent,
    queryParams,
    location.state?.filterLabel,
  );
  const blogHeading = getExploreBlogHeading(
    intent,
    queryParams,
    location.state?.filterLabel,
  );
  const articleKey = getExploreArticleKey(
    queryParams,
    location.state?.filterLabel,
  );
  const isShortletPage =
    location.pathname.includes("/shortlet") ||
    location.pathname.includes("/properties");

  const isShortletRoute =
    location.pathname === "/shortlet" || location.pathname === "/shortlet/";

  const listingLocation = (() => {
    const key = queryParams.get("key");
    if (key && !isExplorePriceKey(key)) return key;

    const filterLabel = location.state?.filterLabel;
    if (
      filterLabel &&
      filterLabel !== "1M & Below" &&
      filterLabel !== "1M & Above"
    ) {
      return filterLabel.toLowerCase();
    }

    return undefined;
  })();

  return user && user?._id ? (
    <DashboardLayout
      title="Explore"
      subtitle="Select and book property on krent"
    >
      <GetMeta type={intent} />
      <KycBanner loading={loadingkyc} show={!userKyc} />

      <ExploreSearchSection
        intent={intent}
        page={page}
        setPage={setPage}
        filtersLoaded={filtersLoaded}
        setFiltersLoaded={setFiltersLoaded}
        onTypeChange={handleTypeChange}
        user={user}
        limit={limit}
      />
      <ExplorePropertyList
        loading={loading}
        data={data}
        page={page}
        setPage={setPage}
        intent={intent}
        customTitle={listingTitle}
        showTitle={Boolean(listingTitle)}
        showListingSubtitle={isShortletRoute}
        listingLocation={listingLocation}
      />

      <ExploreFiltersOffcanvas
        isLoaded={isLoaded}
        autoCompleteLocation={autoCompleteLocation}
        setAutoCompleteLocation={setAutoCompleteLocation}
        onLoadState={onLoadState}
        onStateChanged={onStateChanged}
        page={page}
        limit={limit}
        setPage={setPage}
        setFiltersLoaded={setFiltersLoaded}
      />

      <TransactionModal
        show={showModal}
        onClose={handleCloseModal}
        inprogressTransactions={inprogressTransactions}
        pendingPaymentTransactions={pendingPaymentTransactions}
        pendingInspectionTransactions={pendingInspectionTransactions}
      />
    </DashboardLayout>
  ) : (
    <BaseLayout>
      <GetMeta type={intent} />
      <div className="px-3 px-md-5" style={{ marginTop: "8rem" }}>
        <ExploreSearchSection
          intent={intent}
          page={page}
          setPage={setPage}
          filtersLoaded={filtersLoaded}
          setFiltersLoaded={setFiltersLoaded}
          onTypeChange={handleTypeChange}
          isMobile
          limit={limit}
        />

        <ExplorePropertyList
          loading={loading}
          data={data}
          page={page}
          setPage={setPage}
          showQuickLinks
          showTitle
          intent={intent}
          customTitle={listingTitle}
          showListingSubtitle={isShortletRoute}
          listingLocation={listingLocation}
        />
      </div>

      <section className="container py-5 px-0 px-md-3">
        <PopularLocation />
      </section>

      <ExploreBlogSection
        news={news}
        articleKey={articleKey}
        showShortletArticles={isShortletPage && !articleKey}
        heading={blogHeading}
      />

      <ExploreFiltersOffcanvas
        isLoaded={isLoaded}
        autoCompleteLocation={autoCompleteLocation}
        setAutoCompleteLocation={setAutoCompleteLocation}
        onLoadState={onLoadState}
        onStateChanged={onStateChanged}
        page={page}
        limit={limit}
        setPage={setPage}
        setFiltersLoaded={setFiltersLoaded}
      />
    </BaseLayout>
  );
};
